import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, Banknote, FileText, Save } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

// M3X.COMP F.B.2 — HR-initiated comp change form.
// Per M30 pattern: panels include AI assist hints + DET amount preview.
// Money math is DET (Commandment 3). Sonnet reasons are wired in PR 18.

interface FormState {
  employee_id: string;
  change_type: string;
  change_reason: string;
  effective_date: string;
  previous_basic_salary: string;
  new_basic_salary: string;
}

const EMPTY_FORM: FormState = {
  employee_id: "",
  change_type: "salary_increase",
  change_reason: "",
  effective_date: new Date().toISOString().slice(0, 10),
  previous_basic_salary: "",
  new_basic_salary: "",
};

const CHANGE_TYPES = [
  "salary_increase",
  "salary_decrease",
  "promotion",
  "demotion",
  "market_adjustment",
  "bonus",
  "commission_structure_change",
];

function decimalOrNull(s: string): number | null {
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function CompChangeNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  // Naive employee picker — list every active employee in the LE.
  // PR 11 ships the basic form; PR 14 deepens with autocomplete.
  const employeesQ = useQuery({
    queryKey: ["employees-picker", legalEntityId],
    queryFn: () =>
      api.listEmployeesForPicker({
        legalEntityId: legalEntityId!,
        limit: 500,
      }),
    enabled: !!legalEntityId,
  });
  const employees = employeesQ.data ?? [];

  const create = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        legal_entity_id: legalEntityId,
        employee_id: form.employee_id,
        change_type: form.change_type,
        change_reason: form.change_reason || null,
        effective_date: form.effective_date,
      };
      if (form.previous_basic_salary)
        body.previous_basic_salary = form.previous_basic_salary;
      if (form.new_basic_salary)
        body.new_basic_salary = form.new_basic_salary;
      return api.createCompChange(body);
    },
    onSuccess: (created) => {
      setError(null);
      // Auto-submit so the workflow moves out of draft.
      api.compChangeSubmit(created.id).finally(() => {
        navigate(`/operations/transactions/comp/${created.id}`);
      });
    },
    onError: (e: Error) => setError(e.message),
  });

  const delta = useMemo(() => {
    const prev = decimalOrNull(form.previous_basic_salary);
    const next = decimalOrNull(form.new_basic_salary);
    if (prev === null || next === null) return null;
    const d = next - prev;
    const pct = prev > 0 ? (d / prev) * 100 : null;
    return { delta: d, pct };
  }, [form.previous_basic_salary, form.new_basic_salary]);

  const isDecrease =
    form.change_type === "salary_decrease" ||
    (delta !== null && delta.delta < 0);

  if (gate) return gate;
  if (employeesQ.isPending) return <Loading />;
  if (employeesQ.isError)
    return <ErrorPanel message={(employeesQ.error as Error).message} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("comp_change_new.title")}
        subtitle={t("comp_change_new.subtitle")}
      />

      {/* AI assist panel — placeholder for PR 25 cycle planner narrative */}
      <div className="rounded border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900">
        <div className="flex items-center gap-2 font-medium">
          <Banknote className="h-3 w-3" />
          {t("comp_change_new.ai_assist_title")}
        </div>
        <div className="mt-1">
          {t("comp_change_new.ai_assist_body")}
        </div>
      </div>

      <div className="space-y-4 rounded border border-slate-200 bg-white p-4">
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-slate-700">
            {t("comp_change_new.identity")}
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col text-xs">
              <span className="text-slate-600">{t("comp_change_new.employee")}</span>
              <select
                value={form.employee_id}
                onChange={(e) =>
                  setForm({ ...form, employee_id: e.target.value })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              >
                <option value="">—</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.employee_code} —{" "}
                    {e.first_name_en} {e.last_name_en}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-xs">
              <span className="text-slate-600">
                {t("comp_change_new.change_type")}
              </span>
              <select
                value={form.change_type}
                onChange={(e) =>
                  setForm({ ...form, change_type: e.target.value })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              >
                {CHANGE_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-slate-700">
            {t("comp_change_new.amounts")}
          </legend>
          <div className="grid grid-cols-3 gap-3">
            <label className="flex flex-col text-xs">
              <span className="text-slate-600">
                {t("comp_change_new.previous")}
              </span>
              <input
                type="number"
                step="0.01"
                min={0}
                value={form.previous_basic_salary}
                onChange={(e) =>
                  setForm({
                    ...form,
                    previous_basic_salary: e.target.value,
                  })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="flex flex-col text-xs">
              <span className="text-slate-600">{t("comp_change_new.new")}</span>
              <input
                type="number"
                step="0.01"
                min={0}
                value={form.new_basic_salary}
                onChange={(e) =>
                  setForm({ ...form, new_basic_salary: e.target.value })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="flex flex-col text-xs">
              <span className="text-slate-600">
                {t("comp_change_new.effective_date")}
              </span>
              <input
                type="date"
                value={form.effective_date}
                onChange={(e) =>
                  setForm({ ...form, effective_date: e.target.value })
                }
                className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
          </div>

          {delta !== null && (
            <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs">
              <div className="font-medium">
                {t("comp_change_new.delta_label")}:{" "}
                <span className={delta.delta < 0 ? "text-rose-700" : "text-emerald-700"}>
                  {delta.delta >= 0 ? "+" : ""}
                  {delta.delta.toFixed(2)} SAR
                  {delta.pct !== null
                    ? ` (${delta.pct >= 0 ? "+" : ""}${delta.pct.toFixed(2)}%)`
                    : ""}
                </span>
              </div>
            </div>
          )}

          {isDecrease && (
            <div className="rounded border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900">
              <div className="flex items-center gap-2 font-medium">
                <FileText className="h-3 w-3" />
                {t("comp_change_new.article_60_warning_title")}
              </div>
              <div className="mt-1">
                {t("comp_change_new.article_60_warning_body")}
              </div>
            </div>
          )}
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-slate-700">
            {t("comp_change_new.reason")}
          </legend>
          <textarea
            rows={3}
            value={form.change_reason}
            onChange={(e) =>
              setForm({ ...form, change_reason: e.target.value })
            }
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder={t("comp_change_new.reason_placeholder")}
          />
        </fieldset>

        {error && (
          <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/operations/transactions/comp")}
            className="text-xs text-slate-600 hover:underline"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={
              !form.employee_id || !form.effective_date || create.isPending
            }
            onClick={() => create.mutate()}
            className="inline-flex items-center gap-2 rounded bg-brand-deep px-4 py-2 text-sm text-white hover:bg-slate-900 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {create.isPending
              ? t("common.saving")
              : t("comp_change_new.submit")}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
